export function computeEntity(graph, cellId) {
  const cell = graph.getCell(cellId);
  if (!cell) return undefined;

  if (cell.isLink()) {
    const src = graph.getCell(cell.get('source')?.id);
    const tgt = graph.getCell(cell.get('target')?.id);
    const srcName = src?.get?.('cellName') || src?.id;
    const tgtName = tgt?.get?.('cellName') || tgt?.id;
    return `T_${srcName}_${tgtName}`;
  } else {
    const full = cell.attributes.customAttributes.fullName;
    const name = cell.get?.('cellName') || cell.attributes.cellName;
    if (!full) return undefined;
    // Normalize "Tri_" prefix to "Sorter_" for simulation compatibility
    const triLike = /^Tri[^_]*_/i;
    const normalized = triLike.test(full) ? full.replace(triLike, 'Sorter_') : full;
    return `${normalized}${name}`;
  }
}

/**
 * Refresh the `entity` field on all outages entries for a specific cell.
 */
export function refreshOutagesEntity(graph, cellId) {
  if (!graph.attributes.tables) graph.attributes.tables = {};
  const outages = Array.isArray(graph.attributes.tables.outages) ? graph.attributes.tables.outages : [];

  graph.attributes.tables.outages = outages.map((o) => {
    if (o.cellId === cellId) {
      return { ...o, entity: computeEntity(graph, cellId) };
    }
    return o;
  });
}

export function updatePorts(graph, newPorts, cellId) {
  const cell = graph.getCell(cellId);
  if (!cell) return;

  try {
    cell.set('ports', newPorts, { silent: true });
  } catch (error) {
    console.error('Error updating ports:', error);
  }
}

export function updatePortLinks(graph, port, cellId) {
  const cell = graph.getCell(cellId);
  const existingLinks = graph.getConnectedLinks(cell, {
    outbound: true,
    port: port.id,
  });
  existingLinks.forEach((link) => link.remove());
}

export function updateCellProperty(cell, field, path, newValue, options = {}) {
  const oldValue = getDeep(cell.attributes, path) ?? field.defaultValue ?? null;

  const updatedAttributes = { ...cell.attributes };
  setDeep(updatedAttributes, path, newValue);

  // Apply the update to the cell
  cell.set(updatedAttributes, {
    silent: false,
    ...options,
    ui: true,
    inspector: true,
    track: true,
    previous: oldValue,
  });

  if (path === 'cellName' || path === 'customAttributes/cellName') {
    const graph = cell.graph;
    if (graph) {
      refreshOutagesEntity(graph, cell.id);

      if (!cell.isLink()) {
        const connectedLinks = graph.getConnectedLinks(cell, { inbound: true, outbound: true });
        connectedLinks.forEach((link) => {
          refreshOutagesEntity(graph, link.id);
        });
      }
    }
  }
}

export function handlePortChange(service, portId, fieldName, newValue, ports, cellId) {
  const updatedPorts = ports.map((port) => {
    if (port.id === portId) {
      return {
        ...port,
        data: {
          ...port.data,
          [fieldName]: fieldName === 'time' ? Number(newValue) : newValue,
        },
      };
    }
    return port;
  });

  service.updatePorts(updatedPorts, cellId);

  if (fieldName === 'destination') {
    const port = updatedPorts.find((p) => p.id === portId);
    service.updatePortLinks(port, cellId);
  }
}

export function getInitialValue(graph, field) {
  const cell = graph.getCell(field.cellId);
  return cell.get(field.path) ?? field.defaultValue ?? '';
}

export function createDebouncedUpdate(fn, delay = 300) {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
}

// Helper to get deep path
export function getDeep(obj, path) {
  return path.split('/').reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    const index = Number(key);
    return isNaN(index) ? acc[key] : acc[index];
  }, obj);
}

// Helper to set deep path
export function setDeep(obj, path, value) {
  const keys = path.split('/');
  let current = obj;

  keys.forEach((key, index) => {
    const isLast = index === keys.length - 1;
    const nextKey = keys[index + 1];
    const isNextIndex = !isNaN(Number(nextKey));
    const numericKey = Number(key);
    const isArrayIndex = !isNaN(numericKey);

    const resolvedKey = isArrayIndex ? numericKey : key;

    if (isLast) {
      current[resolvedKey] = value;
    } else {
      if (current[resolvedKey] === undefined || current[resolvedKey] === null) {
        current[resolvedKey] = isNextIndex ? [] : {};
      }
      current = current[resolvedKey];
    }
  });
}

/**
 * Update a specific field in a specific outage entry at index `index`.
 */
export function updateOutageEntry(graph, cellId, _, field, value) {
  if (!graph.attributes.tables) graph.attributes.tables = {};
  if (!Array.isArray(graph.attributes.tables.outages)) {
    graph.attributes.tables.outages = [];
  }

  const outages = graph.attributes.tables.outages;
  const index = outages.findIndex((o) => o.cellId === cellId);

  if (index === -1) {
    const entity = computeEntity(graph, cellId);
    const newOutage = {
      cellId,
      entity,
      type: '',
      impact: 1000,
      startTime: 0,
      endTime: 0,
      [field]: value,
    };
    graph.attributes.tables.outages.push(newOutage);
    return;
  }

  // Update existing outage and recompute entity
  outages[index] = {
    ...outages[index],
    [field]: value,
    entity: computeEntity(graph, cellId),
  };
}

/**
 * Add a new outage object to the outages array.
 * Computes and sets the initial `entity` value.
 */
export function addOutageEntry(graph, cellId, newOutage = {}) {
  const cell = graph.getCell(cellId);
  if (!cell) return;

  const outagesPath = 'tables/outages';
  const outages = getDeep(graph.attributes, outagesPath) || [];
  const entity = computeEntity(graph, cellId);
  const updatedOutages = [...outages, { ...newOutage, cellId, entity }];

  const updatedAttributes = { ...graph.attributes };
  setDeep(updatedAttributes, outagesPath, updatedOutages);

  // persist
  graph.set(updatedAttributes, {
    silent: false,
    ui: true,
    inspector: true,
    track: true,
    previous: outages,
  });
}

/**
 * Remove an outage entry at a specific index.
 */
export function removeOutageEntry(graph, cellId, index) {
  const outagesPath = 'tables/outages';
  const outages = getDeep(graph.attributes, outagesPath) || [];
  const updatedOutages = outages.filter((_, i) => i !== index);

  const updatedAttributes = { ...graph.attributes };
  setDeep(updatedAttributes, outagesPath, updatedOutages);

  graph.set(updatedAttributes, {
    silent: false,
    ui: true,
    inspector: true,
    track: true,
    previous: outages,
  });
}
